"use client";

import React, { Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, Transition } from "@headlessui/react";
import { 
  ArrowRightOnRectangleIcon,
  CogIcon,
  DocumentDuplicateIcon,
  CircleStackIcon,
  PresentationChartBarIcon,
  CreditCardIcon,
} from "@heroicons/react/24/solid";
import { useAuth } from "src/contexts/AuthContext";
import ExtensionBanner from "src/components/ExtensionBanner";

interface PageHeaderProps {
  title: string;
  subtitle: string;
  backLink?: {
    href: string;
    label: string;
  };
  verifyLink?: boolean;
  configLink?: boolean;
  batchVerifyLink?: boolean;
  tokenUsageLink?: boolean;
  analysisLink?: boolean;
}

function ProfileSkeleton() {
  return (
    <div className="flex items-center space-x-2 animate-pulse">
      <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
      <div className="h-4 w-24 bg-gray-300 rounded"></div>
    </div>
  );
}

export default function PageHeader({ configLink = false, batchVerifyLink = false, tokenUsageLink = false, analysisLink = false }: PageHeaderProps) {
  const { user, isLoading, signOut, isAdmin } = useAuth();

  return (
    <div>
      {/* Top Navigation Bar */}
      <div className="flex items-start justify-between py-4 mb-6 border-b border-gray-100">
        <Link href="/app" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
          <Image src="/logo-black.svg" alt="Mira Logo" width={24} height={24} />
          <span className="text-md font-medium font-semibold text-gray-700"> mira verify</span>
        </Link>
        
        <div className="flex items-center space-x-4">
          {/* Extension Status Indicator */}
          <div className="hidden md:block mr-3">
            <ExtensionBanner />
          </div>
          
          {/* Navigation Links - Desktop only */}
          {(configLink || batchVerifyLink || tokenUsageLink || analysisLink) && (
            <div className="hidden md:flex items-center space-x-1 mr-4">
              {analysisLink && (
                <Link
                  href="/app/analysis"
                  className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  title="Analysis Dashboard"
                >
                  <PresentationChartBarIcon className="h-4 w-4 mr-1.5" />
                  Analysis
                </Link>
              )}
              {tokenUsageLink && (
                <Link
                  href="/app/token-usage"
                  className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  title="Token Usage"
                >
                  <CircleStackIcon className="h-4 w-4 mr-1.5" />
                  Usage
                </Link>
              )}
              {isAdmin && (
                <Link
                  href="/admin/users"
                  className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  title="Admin Dashboard"
                >
                  <CogIcon className="h-4 w-4 mr-1.5" />
                  Admin
                </Link>
              )}
              {batchVerifyLink && (
                <Link
                  href="/app/batch-verify"
                  className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  title="Batch Verify"
                >
                  <DocumentDuplicateIcon className="h-4 w-4 mr-1.5" />
                  Batch
                </Link>
              )}
            </div>
          )}
          
          {isLoading ? (
            <ProfileSkeleton />
          ) : user ? (
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none group">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-100 focus:outline-none ring-1 ring-black ring-opacity-5">
                  {/* Mobile Navigation Links */}
                  {analysisLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          href="/app/analysis"
                          className={`md:hidden flex items-center w-full px-4 py-2 text-sm text-gray-700 ${
                            active ? 'bg-gray-50' : ''
                          }`}
                        >
                          <PresentationChartBarIcon className="h-4 w-4 mr-3 text-gray-400" />
                          Analysis
                        </Link>
                      )}
                    </Menu.Item>
                  )}
                  {tokenUsageLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          href="/app/token-usage"
                          className={`md:hidden flex items-center w-full px-4 py-2 text-sm text-gray-700 ${
                            active ? 'bg-gray-50' : ''
                          }`}
                        >
                          <CircleStackIcon className="h-4 w-4 mr-3 text-gray-400" />
                          Usage
                        </Link>
                      )}
                    </Menu.Item>
                  )}
                  {isAdmin && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          href="/admin/users"
                          className={`md:hidden flex items-center w-full px-4 py-2 text-sm text-gray-700 ${
                            active ? 'bg-gray-50' : ''
                          }`}
                        >
                          <CogIcon className="h-4 w-4 mr-3 text-gray-400" />
                          Admin
                        </Link>
                      )}
                    </Menu.Item>
                  )}
                  {batchVerifyLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          href="/app/batch-verify"
                          className={`md:hidden flex items-center w-full px-4 py-2 text-sm text-gray-700 ${
                            active ? 'bg-gray-50' : ''
                          }`}
                        >
                          <DocumentDuplicateIcon className="h-4 w-4 mr-3 text-gray-400" />
                          Batch
                        </Link>
                      )}
                    </Menu.Item>
                  )}
                  {/* Divider for mobile */}
                  {(analysisLink || configLink || batchVerifyLink || tokenUsageLink) && (
                    <div className="md:hidden border-t border-gray-100 my-1"></div>
                  )}
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        href="/app/billing"
                        className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 ${
                          active ? 'bg-gray-50' : ''
                        }`}
                      >
                        <CreditCardIcon className="h-4 w-4 mr-3 text-gray-400" />
                        Billing
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        href="/app/settings"
                        className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 ${
                          active ? 'bg-gray-50' : ''
                        }`}
                      >
                        <CogIcon className="h-4 w-4 mr-3 text-gray-400" />
                        Settings
                      </Link>
                    )}
                  </Menu.Item>
                  <div className="border-t border-gray-100 my-1"></div>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={signOut}
                        className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 ${
                          active ? 'bg-gray-50' : ''
                        }`}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-gray-400" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          ) : (
            <Link
              href="/login"
              className="text-gray-700 hover:text-gray-900 text-sm font-medium flex items-center space-x-1"
            >
              <span>Sign in</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}